import { useState } from "react";
import { VideoUpload } from "./VideoUpload";
import { supabase } from "./supabase";

const C = {
  surface: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.10)",
  accent: "#60A5FA", accentGlow: "rgba(96,165,250,0.15)",
  gold: "#F0A500", green: "#34D399", red: "#F87171",
  text: "#FFFFFF", textMuted: "#94A3B8",
};
const F = { display: "'Playfair Display', Georgia, serif", body: "'DM Sans', sans-serif" };

const CATEGORIES = ["AI Workflows","No-Code","Short Video","Solopreneur","Design","Productivity"];
const LEVELS = ["Beginner","Intermediate","Advanced"];

export function UploadCourseForm({ userId, onSuccess }) {
  const [form, setForm] = useState({
    title: "", category: "AI Workflows", description: "",
    price: "", level: "Beginner",
  });
  const [videoData, setVideoData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canSubmit = form.title && form.description && form.category && videoData;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      const { error: dbError } = await supabase.from("courses").insert({
        creator_id: userId,
        title: form.title,
        category: form.category,
        description: form.description,
        price: parseFloat(form.price) || 0,
        level: form.level,
        mux_playback_id: videoData.mux_playback_id,
        mux_asset_id: videoData.mux_asset_id,
        thumbnail: videoData.thumbnail_url,
        duration: videoData.duration
          ? `${Math.round(videoData.duration / 60)} min`
          : null,
        status: "pending",
      });

      if (dbError) throw dbError;
      setSubmitted(true);
      setTimeout(() => onSuccess && onSuccess(), 2000);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h3 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
          Course Submitted!
        </h3>
        <p style={{ color: C.textMuted, fontSize: 14 }}>
          Your course is under review. We'll notify you within 5 business days.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <h3 style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, marginBottom: 24 }}>
        Upload a New Course
      </h3>

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

        {/* Title */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>
            Course Title <span style={{ color: C.red }}>*</span>
          </label>
          <input className="input-field" placeholder="e.g. Master ChatGPT Agents in 1 Hour"
            value={form.title} onChange={e => update("title", e.target.value)} />
        </div>

        {/* Category + Level */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>Category</label>
            <select className="input-field" value={form.category} onChange={e => update("category", e.target.value)}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>Level</label>
            <select className="input-field" value={form.level} onChange={e => update("level", e.target.value)}>
              {LEVELS.map(l => <option key={l}>{l}</option>)}
            </select>
          </div>
        </div>

        {/* Price */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>
            Price ($) <span style={{ color: C.textMuted, fontWeight: 400 }}>— leave blank for free</span>
          </label>
          <input className="input-field" placeholder="e.g. 29" type="number" min="0"
            value={form.price} onChange={e => update("price", e.target.value)} style={{ maxWidth: 160 }} />
        </div>

        {/* Description */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>
            Description <span style={{ color: C.red }}>*</span>
          </label>
          <textarea className="input-field" rows={4}
            placeholder="What will students learn? What practical skill will they have by the end?"
            value={form.description} onChange={e => update("description", e.target.value)}
            style={{ resize: "vertical" }} />
        </div>

        {/* Video Upload */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>
            Course Video <span style={{ color: C.red }}>*</span>
          </label>
          <VideoUpload onUploadComplete={(data) => {
            setVideoData(data);
          }} />
        </div>

        {/* Video preview after upload */}
        {videoData && (
          <div style={{ padding: "14px 16px", borderRadius: 10, background: C.accentGlow, border: `1px solid ${C.accent}30` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.green, marginBottom: 4 }}>✅ Video uploaded successfully</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>
              Playback ID: {videoData.mux_playback_id?.slice(0, 20)}...
              {videoData.duration && ` · ${Math.round(videoData.duration)}s`}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(232,89,60,0.1)", border: `1px solid rgba(232,89,60,0.3)` }}>
            <div style={{ fontSize: 13, color: C.red }}>⚠️ {error}</div>
          </div>
        )}

        {/* Submit */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", paddingTop: 4 }}>
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            style={{ padding: "13px 28px", opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? "pointer" : "not-allowed" }}
          >
            {submitting ? "Submitting..." : "Submit for Review"}
          </button>
          {!canSubmit && (
            <span style={{ fontSize: 12, color: C.textMuted }}>
              {!form.title ? "Add a title" : !form.description ? "Add a description" : !videoData ? "Upload a video" : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}